import React, { useState, useEffect } from "react";
import { Table, Button, Badge, Card, Row, Col, Spinner, Form } from "react-bootstrap";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import PaymentDetailsModal from "./PaymentDetailsModal";

const PaymentAdminManagement = () => {
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 2).toISOString().split('T')[0];
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString().split('T')[0];
  };

  const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'asc' });
  const [targets, setTargets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [approvalSteps, setApprovalSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  
  const [filters, setFilters] = useState({
    fromDate: getFirstDayOfMonth(),
    toDate: getLastDayOfMonth(),
    status: "",
    departmentId: "",
    projectId: "",
    businessUnit: "",
    costType: "",
    paymentTarget: "",
    currentStep: "",
    targetId: "",
    title: "",
    internalDocumentType: ""
  });

  const PAGE_SIZE = 10;
  
  const handleReset = () => {
    setFilters({
      fromDate: getFirstDayOfMonth(),
      toDate: getLastDayOfMonth(),
      status: "",
      departmentId: "",
      projectId: "",
      businessUnit: "",
      costType: "",
      paymentTarget: "",
      currentStep: "",
      targetId: "",
      title: "",
      internalDocumentType: ""
    });
    setCurrentPage(1);
  };

  const sortedRequests = [...requests].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];

    if (sortConfig.key === 'createdBy') {
      aVal = a.createdBy?.employeeLeaveDetail?.fullName || "";
      bVal = b.createdBy?.employeeLeaveDetail?.fullName || "";
    }
    
    aVal = aVal ?? "";
    bVal = bVal ?? "";

    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const indexOfLastItem = currentPage * PAGE_SIZE;
  const indexOfFirstItem = indexOfLastItem - PAGE_SIZE;
  const currentItems = sortedRequests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedRequests.length / PAGE_SIZE);

  const totalVndAmount = requests
    .filter(item => item.currency && item.currency.toUpperCase() === "VND")
    .reduce((sum, item) => sum + (item.totalAmount || 0), 0);

  const totalAudAmount = requests
    .filter(item => item.currency && item.currency.toUpperCase() === "AUD")
    .reduce((sum, item) => sum + (item.totalAmount || 0), 0);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [deptRes, projRes, configRes] = await Promise.all([
          axiosClient.get(API_ENDPOINTS.departments),
          axiosClient.get(API_ENDPOINTS.projects),
          axiosClient.get(API_ENDPOINTS.paymentApprovalConfigs)
        ]);
        setDepartments(deptRes.data || []);
        setProjects(projRes.data || []);
        setApprovalSteps(configRes.data || []);
      } catch (err) {
        console.error("Error loading metadata:", err);
      }
    };
    fetchMetadata();
  }, []);

  useEffect(() => {
    const fetchTargetList = async () => {
      if (filters.paymentTarget !== "") {
        try {
          const res = await axiosClient.get(API_ENDPOINTS.getSuppliersByType(parseInt(filters.paymentTarget)));
          setTargets(res.data || []);
        } catch (err) {
          console.error("Error loading target list:", err);
          setTargets([]);
        }
      } else {
        setTargets([]);
      }
    };
    fetchTargetList();
  }, [filters.paymentTarget]);

  useEffect(() => {
    fetchPaymentRequests();
  }, [filters]);

  const fetchPaymentRequests = async () => {
    setLoading(true);
    setCurrentPage(1);
    try {
      const payload = { ...filters, supplierId: filters.targetId };
      const url = API_ENDPOINTS.getPaymentAdminList(payload);
      const res = await axiosClient.get(url);
      setRequests(res.data || []);
    } catch (err) { 
      console.error("Load error:", err); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const filteredIds = requests.map(item => item.id);
      if (filteredIds.length === 0) {
        alert("No data available to export!");
        setExportLoading(false);
        return;
      }
      const url = API_ENDPOINTS.exportPaymentExcel(filteredIds, filters.fromDate, filters.toDate);
      const response = await axiosClient.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      const dateString = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      link.setAttribute("download", `Danh_Sach_De_Nghi_Thanh_Toan_${dateString}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Export Excel error:", err);
      alert("An error occurred during Excel export.");
    } finally {
      setExportLoading(false);
    }
  };

  const getTargetLabel = (id) => {
    if (!id || targets.length === 0) return "Select Target Object";
    const found = targets.find(t => t && t.id && t.id.toString() === id.toString());
    return found ? (found.supplierName || found.name || "N/A") : "Select Target Object";
  };

  const getPaymentTargetLabel = (target) => {
    const targetMap = { 0: "Nhà cung cấp", 1: "Khách hàng", 2: "Nội bộ" };
    return targetMap[target] ?? "N/A";
  };

  const fetchRequestDetails = async (id) => {
    try {
      const res = await axiosClient.get(`/PaymentRequests/${id}`);
      setCurrentRequest(res.data);
      setShowModal(true);
    } catch (err) {
      console.error("Error fetching request details:", err);
      alert("Could not load request details.");
    }
  };

  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  const renderStatus = (status) => {
    const statusMap = { 0: ["DRAFT", "secondary"], 1: ["PENDING", "warning"], 2: ["APPROVED", "success"], 3: ["REJECTED", "danger"] };
    const [label, bg] = statusMap[status] || ["UNKNOWN", "dark"];
    return <Badge bg={bg} className="rounded-pill px-3 py-1">{label}</Badge>;
  };

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold text-dark mb-0 text-uppercase">Payment Admin Management</h3>
        <Button variant="success" onClick={handleExportExcel} disabled={exportLoading || loading} className="d-flex align-items-center gap-2 shadow-sm">
          {exportLoading ? <><Spinner animation="border" size="sm" /> Exporting...</> : <><i className="fas fa-file-excel"></i> EXPORT EXCEL</>}
        </Button>
      </div>

      <Card className="shadow-sm border-0 rounded-3 mb-4">
        <Card.Body className="p-3">
          {/* Row 1: Main Filters */}
          <Row className="g-3">
            <Col md={2}><Form.Label className="small fw-bold text-muted">FROM DATE</Form.Label><Form.Control type="date" value={filters.fromDate} onChange={(e) => setFilters(p => ({...p, fromDate: e.target.value}))} size="sm" /></Col>
            <Col md={2}><Form.Label className="small fw-bold text-muted">TO DATE</Form.Label><Form.Control type="date" value={filters.toDate} onChange={(e) => setFilters(p => ({...p, toDate: e.target.value}))} size="sm" /></Col>
            <Col md={2}><Form.Label className="small fw-bold text-muted">STATUS</Form.Label>
              <Form.Select value={filters.status} onChange={(e) => setFilters(p => ({...p, status: e.target.value}))} size="sm">
                <option value="">All</option><option value="1">Pending</option><option value="2">Approved</option><option value="3">Rejected</option>
              </Form.Select>
            </Col>
            <Col md={2}><Form.Label className="small fw-bold text-muted">DEPARTMENT</Form.Label>
              <Form.Select value={filters.departmentId} onChange={(e) => setFilters(p => ({...p, departmentId: e.target.value}))} size="sm">
                <option value="">All Departments</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}><Form.Label className="small fw-bold text-muted">PROJECT</Form.Label>
              <Form.Select value={filters.projectId} onChange={(e) => setFilters(p => ({...p, projectId: e.target.value}))} size="sm">
                <option value="">All Projects</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Form.Select>
            </Col>
          </Row>

          {/* Row 2: Secondary Filters */}
          <Row className="g-3 mt-1">
            <Col md={2}><Form.Label className="small fw-bold text-muted">CURRENT STEP</Form.Label>
              <Form.Select value={filters.currentStep} onChange={(e) => setFilters(p => ({...p, currentStep: e.target.value}))} size="sm">
                <option value="">All Steps</option>
                {approvalSteps.map(s => <option key={s.id} value={s.stepOrder}>{s.stepOrder} - {s.note}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}><Form.Label className="small fw-bold text-muted">B.U</Form.Label>
              <Form.Select value={filters.businessUnit} onChange={(e) => setFilters(p => ({...p, businessUnit: e.target.value}))} size="sm">
                <option value="">All</option>
                <option value="PEX">PEX</option><option value="GOZA">GOZA</option><option value="LUXCOM">LUXCOM</option><option value="PICON">PICON</option>
              </Form.Select>
            </Col>
            <Col md={3}><Form.Label className="small fw-bold text-muted">TARGET TYPE</Form.Label>
              <Form.Select value={filters.paymentTarget} onChange={(e) => setFilters(p => ({...p, paymentTarget: e.target.value, targetId: ""}))} size="sm">
                <option value="">All Types</option>
                <option value="0">Nhà cung cấp</option><option value="1">Khách hàng</option><option value="2">Nội bộ</option>
              </Form.Select>
            </Col>
            <Col md={3}><Form.Label className="small fw-bold text-muted">OBJECT</Form.Label>
              <Form.Select value={filters.targetId} onChange={(e) => setFilters(p => ({...p, targetId: e.target.value}))} size="sm" disabled={!filters.paymentTarget}>
                <option value="">{getTargetLabel(filters.targetId)}</option>
                {targets.map(t => <option key={t.id} value={t.id}>{t.supplierName || t.name}</option>)}
              </Form.Select>
            </Col>
          </Row>

          {/* Row 3: Search and Reset */}
          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Label className="small fw-bold text-muted">SEARCH TITLE</Form.Label>
              <Form.Control type="text" placeholder="Search by title..." value={filters.title} onChange={(e) => setFilters(p => ({...p, title: e.target.value}))} size="sm" />
            </Col>
            <Col md={4}>
              <Form.Label className="small fw-bold text-muted">INTERNAL DOCUMENT TYPE</Form.Label>
              <Form.Select value={filters.internalDocumentType} onChange={(e) => setFilters(p => ({...p, internalDocumentType: e.target.value}))} size="sm">
                <option value="">All Document Types</option>
                <option value="Thanh toán">Đề nghị thanh toán</option>
                <option value="Tạm ứng">Tạm ứng</option>
                <option value="Hoàn ứng">Hoàn ứng</option>
                <option value="Khác">Khác</option>
              </Form.Select>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button variant="outline-danger" size="sm" className="w-100" onClick={handleReset}>RESET FILTERS</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0 rounded-3">
        <Table hover responsive className="mb-0 align-middle">
          <thead className="bg-primary text-white">
            <tr>
              <th className="ps-4">#</th>
              {[
                { label: "TITLE", key: "title" },
                { label: "CREATED BY", key: "createdBy" },
                { label: "B.U", key: "businessUnit" },
                { label: "COST TYPE", key: "costType" },
                { label: "TARGET", key: "paymentTarget" },
                { label: "AMOUNT", key: "totalAmount" },
                { label: "STATUS", key: "status" }
              ].map((col) => (
                <th 
                  key={col.key} 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleSort(col.key)}
                >
                  {col.label} {sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ' ↕'}
                </th>
              ))}
              <th className="text-center">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="text-center py-5"><Spinner animation="border" /></td></tr>
            ) : currentItems.length > 0 ? (
              currentItems.map((r, idx) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td className="ps-4 fw-bold">{indexOfFirstItem + idx + 1}</td>
                    <td className="fw-bold text-primary">{r.title}</td>
                    <td>{r.createdBy?.employeeLeaveDetail?.fullName || "N/A"}</td>
                    <td><Badge bg="light" text="dark">{r.businessUnit || "N/A"}</Badge></td>
                    <td>{r.costType || "N/A"}</td>
                    <td><Badge bg="info" text="dark" className="bg-opacity-25">{getPaymentTargetLabel(r.paymentTarget)}</Badge></td>
                    <td>{r.totalAmount?.toLocaleString()} {r.currency}</td>
                    <td>{renderStatus(r.status)}</td>
                    <td className="text-center">
                      <Button variant="info" size="sm" className="text-white mb-1" onClick={() => fetchRequestDetails(r.id)}><i className="fas fa-eye"></i></Button>
                      <div className="small"><Button variant="link" size="sm" className="text-decoration-none p-0" onClick={() => toggleRow(r.id)}>{expandedRows[r.id] ? "[-] Hide Logs" : "[+] View Logs"}</Button></div>
                    </td>
                  </tr>
                  {expandedRows[r.id] && (
                    <tr className="bg-light">
                      <td colSpan="9" className="p-3">
                        <Table size="sm" className="mb-0 bg-white shadow-sm">
                          <thead className="table-light">
                            <tr><th>Step</th><th>Approver</th><th>Status</th><th>Comment</th><th>Processed At</th></tr>
                          </thead>
                          <tbody>
                            {(r.approvalLogs || []).map(log => (
                              <tr key={log.id}>
                                <td>Step {log.stepOrder}</td>
                                <td>{log.approver?.employeeLeaveDetail?.fullName || "N/A"}</td>
                                <td><Badge bg={log.status === 'Approved' ? 'success' : log.status === 'Rejected' ? 'danger' : 'warning'}>{log.status || 'Pending'}</Badge></td>
                                <td>{log.comment || "-"}</td>
                                <td>{log.processedAt ? new Date(log.processedAt).toLocaleString() : "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center py-5 text-muted">
                  No payment requests found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        
        {/* Pagination giao diện mới */}
        {totalPages > 1 && (
          <div className="card-footer bg-white border-0 py-3 text-center">
            <button 
              className="btn btn-sm btn-light mx-1 border shadow-sm rounded-circle" 
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <span className="mx-3 fw-bold text-primary small">Page {currentPage} of {totalPages}</span>
            <button 
              className="btn btn-sm btn-light mx-1 border shadow-sm rounded-circle" 
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        )}

        <Card.Footer className="bg-light p-3">
          <Row className="justify-content-end text-end g-3">
            <Col xs={6} sm={4} md={3}>
              <div className="small fw-bold text-muted text-uppercase mb-1">Total AUD Amount</div>
              <h5 className="mb-0 text-success fw-bold">{totalAudAmount.toLocaleString()} AUD</h5>
            </Col>
            <Col xs={6} sm={4} md={3} className="border-start">
              <div className="small fw-bold text-muted text-uppercase mb-1">Total VND Amount</div>
              <h5 className="mb-0 text-primary fw-bold">{totalVndAmount.toLocaleString()} VND</h5>
            </Col>
          </Row>
        </Card.Footer>
      </Card>

      <PaymentDetailsModal show={showModal} onHide={() => setShowModal(false)} request={currentRequest} />
    </div>
  );
};

export default PaymentAdminManagement;